/**
 * StateSelector Component
 *
 * Purpose: Displays all 8 Australian states/territories as selectable options
 * User Flow: This is Step 1 - User selects their state
 * Data Source: stateData.js
 */

import React, { useState } from 'react';
import { useCalculatorContext } from '../contexts/CalculatorContext';
import { stateData } from '../data';

const StateSelector = () => {
  const { actions, state: contextState } = useCalculatorContext();
  const [hoveredState, setHoveredState] = useState(null);

  // Get all states as array
  const states = Object.values(stateData);

  // Handle state selection
  const handleStateSelect = (stateCode) => {
    const success = actions.setState(stateCode);
    if (success) {
      // Context handles step progression
    }
  };

  return (
    <div className="fss-state-selector">
      <div className="fss-selector-header">
        <h2>Which state or territory do you operate in?</h2>
        <p className="fss-subtitle">
          Select your location to see specific Food Safety Supervisor requirements
        </p>
      </div>

      {/* Display any errors */}
      {contextState.errors && contextState.errors.length > 0 && (
        <div className="fss-error-messages">
          {contextState.errors.map((error, index) => (
            <div key={index} className="fss-error-message">
              <span className="fss-error-icon">⚠️</span>
              {error}
            </div>
          ))}
        </div>
      )}

      {/* State selection grid */}
      <div className="fss-state-grid">
        {states.map((state) => (
          <button
            key={state.code}
            className={`fss-state-button ${contextState.selectedState === state.code ? 'fss-selected' : ''} ${hoveredState === state.code ? 'fss-hovered' : ''}`}
            onClick={() => handleStateSelect(state.code)}
            onMouseEnter={() => setHoveredState(state.code)}
            onMouseLeave={() => setHoveredState(null)}
            aria-label={`Select ${state.name}`}
          >
            <div className="fss-state-code">{state.code}</div>
            <div className="fss-state-name">{state.name}</div>
            
            {/* Show checkmark if selected */}
            {contextState.selectedState === state.code && (
              <div className="fss-state-selected-indicator">
                <span className="fss-checkmark">✓</span>
              </div>
            )}

            {/* Show info icon for special cases */}
            {state.code === 'VIC' && !state.fssRequired && (
              <div className="fss-state-info-badge" title="Not legally mandated">
                <span className="fss-info-icon">ℹ️</span>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* State hover preview - FIXED: Added null check */}
      {hoveredState && stateData[hoveredState] && (
        <div className="fss-state-preview">
          <h4>{stateData[hoveredState].name}</h4>
          <div className="fss-preview-details">
            <div className="fss-preview-item">
              <strong>FSS Required:</strong>{' '}
              {stateData[hoveredState].fssRequired ? 'Yes' : 'Recommended'}
            </div>
            <div className="fss-preview-item">
              <strong>Governing Body:</strong>{' '}
              {stateData[hoveredState].governingBody?.name || 'N/A'}
            </div>
            {stateData[hoveredState].fssRequiredNote && (
              <div className="fss-preview-note">
                <em>{stateData[hoveredState].fssRequiredNote}</em>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Help section */}
      <div className="fss-help-section">
        <p className="fss-help-text">
          <strong>Not sure which state applies?</strong> Select the state where your food business operates.
          If you operate in multiple states, you may need to meet requirements for each location.
        </p>
      </div>
    </div>
  );
};

export default StateSelector;
